import React from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { useToast } from '../../store/ToastContext';
import { 
  Disc, 
  Printer, 
  RotateCw, 
  CheckCircle2 
} from 'lucide-react';

export const UNWIND_DIRECTIONS = [
  { id: 1, name: '#1 Top Out', desc: 'Top edge leads off roll (Outside wound)', icon: '⬆️' },
  { id: 2, name: '#2 Bottom Out', desc: 'Bottom edge leads off roll (Outside wound)', icon: '⬇️' },
  { id: 3, name: '#3 Right Out', desc: 'Right edge leads off roll (Outside wound)', icon: '➡️' },
  { id: 4, name: '#4 Left Out', desc: 'Left edge leads off roll (Outside wound)', icon: '⬅️' },
  { id: 5, name: '#5 Top In', desc: 'Top edge leads off roll (Inside wound)', icon: '🔄' },
  { id: 6, name: '#6 Bottom In', desc: 'Bottom edge leads off roll (Inside wound)', icon: '↩️' },
  { id: 7, name: '#7 Right In', desc: 'Right edge leads off roll (Inside wound)', icon: '↪️' },
  { id: 8, name: '#8 Left In', desc: 'Left edge leads off roll (Inside wound)', icon: '🔁' }
];

export const ROLL_PRINTER_PRESETS = [
  { id: 'epson_c6000', name: 'Epson ColorWorks C6000/C7500', widthMm: 108, maxSpeed: '300 mm/s' },
  { id: 'afinia_l801', name: 'Afinia L801/L901 (Memjet)', widthMm: 216, maxSpeed: '305 mm/s' },
  { id: 'primera_lx910', name: 'Primera LX910/LX3000', widthMm: 210, maxSpeed: '114 mm/s' },
  { id: 'vipcolor_vp700', name: 'VIPColor VP700/VP750', widthMm: 215, maxSpeed: '300 mm/s' },
  { id: 'custom_roll', name: 'Custom Continuous Web Printer', widthMm: 210, maxSpeed: 'Variable' }
];

export const RollLabelModePanel: React.FC = () => {
  const { layout, setLayout } = useSaaSContext();
  const { toast } = useToast();

  const isRollMode = Boolean(layout.isRollMode);
  const selectedPreset = layout.rollPrinterPreset || 'epson_c6000';
  const trackCount = layout.trackCount || 1;
  const unwindDir = layout.unwindDirection || 1;
  const sensorMark = layout.sensorMarkType || 'gap';
  const rollWidthMm = layout.rollWidthMm || 108;

  const handleToggleRollMode = (enabled: boolean) => {
    setLayout(prev => ({
      ...prev,
      isRollMode: enabled,
      trackCount: enabled ? (prev.trackCount || 1) : 1
    }));
    toast.success(
      enabled ? 'Continuous Roll Mode Activated' : 'Sheet Feed Mode Restored',
      enabled 
        ? 'Configured for continuous web roll printers (Epson, Afinia, Primera).' 
        : 'Reverted layout to standard cut sheet media.'
    );
  };

  const handlePresetChange = (presetId: string) => {
    const preset = ROLL_PRINTER_PRESETS.find(p => p.id === presetId);
    if (!preset) return;

    setLayout(prev => ({
      ...prev,
      rollPrinterPreset: presetId as any,
      rollWidthMm: preset.widthMm
    }));
    toast.success('Roll Printer Selected', `Preset set to ${preset.name} (${preset.widthMm}mm web width).`);
  };

  return (
    <div className="flex flex-col gap-4 text-zinc-200">
      
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-amber-950/80 via-zinc-900 to-orange-950/80 p-3.5 rounded-xl border border-amber-500/30 flex items-center justify-between gap-3 shadow-md">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center justify-center shrink-0">
            <Disc className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-white flex items-center gap-1.5">
              Continuous Roll Label Studio
              <span className="text-[10px] bg-amber-500/20 text-amber-300 font-mono px-2 py-0.5 rounded-full border border-amber-500/30">
                Phase 4
              </span>
            </h3>
            <p className="text-xs text-zinc-400">
              Single/Multi-Track roll imposition &amp; Standard #1-#8 Unwind chart for industrial applicators.
            </p>
          </div>
        </div>

        {/* Master Roll Mode Toggle */}
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-950/80 px-3 py-1.5 rounded-lg border border-zinc-800 hover:border-amber-500/50 transition-all">
          <span className="text-xs font-bold text-zinc-300">Roll Feed Mode</span>
          <input 
            type="checkbox"
            checked={isRollMode}
            onChange={(e) => handleToggleRollMode(e.target.checked)}
            className="w-4 h-4 accent-amber-500 cursor-pointer rounded"
          />
        </label>
      </div>

      {/* ROLL CONFIGURATION PANEL */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4">
        
        {/* Printer Hardware Presets */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
            <Printer className="w-4 h-4 text-amber-400" />
            Select Continuous Roll Printer
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ROLL_PRINTER_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePresetChange(p.id)}
                className={`p-3 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                  selectedPreset === p.id
                    ? 'bg-amber-950/60 border-amber-500 text-white shadow-xs'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                }`}
              >
                <div>
                  <div className="text-xs font-bold text-zinc-200">{p.name}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">Max Web: {p.widthMm} mm | Speed: {p.maxSpeed}</div>
                </div>
                {selectedPreset === p.id && <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />}
              </button>
            ))}
          </div>
        </div>

        {/* Track / Lane Count & Sensor Settings */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-zinc-900/80 p-3.5 rounded-xl border border-zinc-800">
          
          {/* Multi-Track Lanes */}
          <div>
            <label className="text-xs font-semibold text-zinc-300 block mb-1">Roll Track Lanes</label>
            <select
              value={trackCount}
              onChange={(e) => setLayout(prev => ({ ...prev, trackCount: parseInt(e.target.value, 10) }))}
              className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2.5 py-1.5 rounded-lg outline-none focus:border-amber-500 font-mono font-bold"
            >
              <option value={1}>1-Track (Single Lane Roll)</option>
              <option value={2}>2-Track (Dual Lane Roll)</option>
              <option value={3}>3-Track (Triple Lane Roll)</option>
              <option value={4}>4-Track (Quad Lane Roll)</option>
            </select>
            <p className="text-[10px] text-zinc-500 mt-1">Lanes across continuous roll web.</p>
          </div>

          {/* Sensor Gap Type */}
          <div>
            <label className="text-xs font-semibold text-zinc-300 block mb-1">Registration Sensor</label>
            <select
              value={sensorMark}
              onChange={(e) => setLayout(prev => ({ ...prev, sensorMarkType: e.target.value as any }))}
              className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2.5 py-1.5 rounded-lg outline-none focus:border-amber-500 font-mono font-bold"
            >
              <option value="gap font-bold">Die-Cut Gap (3mm default)</option>
              <option value="black_mark">Black Mark Sensor Bar</option>
              <option value="notch">Web Notch / Hole Sensor</option>
              <option value="continuous">Continuous Feed (Uncut)</option>
            </select>
            <p className="text-[10px] text-zinc-500 mt-1">Optical eye mark detection.</p>
          </div>

          {/* Roll Web Width */}
          <div>
            <label className="text-xs font-semibold text-zinc-300 block mb-1">Web Roll Width (mm)</label>
            <input 
              type="number"
              value={rollWidthMm}
              onChange={(e) => setLayout(prev => ({ ...prev, rollWidthMm: parseFloat(e.target.value) || 108 }))}
              className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2.5 py-1.5 rounded-lg outline-none focus:border-amber-500 font-mono font-bold"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Substrate web width in mm.</p>
          </div>

        </div>

        {/* Standard Label Unwind Direction Selector (#1 to #8) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
              <RotateCw className="w-4 h-4 text-amber-400" />
              Standard Label Unwind Direction Chart (#1 to #8)
            </label>
            <span className="text-[10px] bg-amber-950 border border-amber-800 text-amber-300 px-2 py-0.5 rounded font-mono font-bold">
              Active: {UNWIND_DIRECTIONS.find(u => u.id === unwindDir)?.name}
            </span>
          </div>

          <p className="text-[11px] text-zinc-400">Required by automatic bottle/pouch applicators to ensure labels peel facing the correct orientation.</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {UNWIND_DIRECTIONS.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setLayout(prev => ({ ...prev, unwindDirection: u.id as any }))}
                className={`p-2.5 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer ${
                  unwindDir === u.id
                    ? 'bg-amber-950/70 border-amber-500 text-white shadow-xs ring-1 ring-amber-500/50'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <span className="text-sm font-bold text-white">{u.icon} {u.name}</span>
                  {unwindDir === u.id && <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" />}
                </div>
                <span className="text-[10px] text-zinc-400 mt-1 leading-tight">{u.desc}</span>
              </button>
            ))}
          </div>
        </div>

      </div>

    </div>
  );
};
